import { useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Calendar, ChevronRight, GraduationCap, Loader2, Search } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Input } from '../../components/ui/input';
import { getAssignedStudents, getPublicRankingSample, type AssignedStudent } from '../../lib/admin-api';
import { teacherHref } from '../../lib/demo-teacher';

/** 公開サンプルの参加者を、担当学生一覧と同じ形に並べ替える（メールは持たない） */
async function getDemoAssignedStudents(): Promise<{ students: AssignedStudent[] }> {
  const ranking = await getPublicRankingSample(0);
  const gradeByClass = new Map(ranking.classes.map((educatorClass) => [educatorClass.id, educatorClass]));
  return {
    students: ranking.students.map((student) => {
      const educatorClass = student.classId == null ? undefined : gradeByClass.get(student.classId);
      return {
        id: student.studentId,
        name: student.name,
        email: '',
        participant_code: student.participantCode,
        group_label: student.groupLabel,
        class_id: student.classId,
        class_name: student.className,
        class_academic_year: educatorClass?.academicYear ?? null,
        class_grade: educatorClass?.grade ?? null,
        class_section: educatorClass?.section ?? null,
        interview_count: student.interviewCount,
        scored_interview_count: student.scoredInterviewCount ?? student.scoreTrend.length,
        last_interview_at: student.lastInterviewAt,
      };
    }),
  };
}

function formatLastActivity(value: string | null): string {
  if (!value) return '面接履歴なし';
  return new Date(value).toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** 教員画面の公開デモ（/demo/teacher/students） */
export function DemoTeacherStudents() {
  return <TeacherStudents demo />;
}

export default function TeacherStudents({ demo = false }: { demo?: boolean } = {}) {
  const [, setLocation] = useLocation();
  const [search, setSearch] = useState('');
  const [grade, setGrade] = useState('');
  const [classId, setClassId] = useState('');
  const { data, isLoading, error } = useQuery({
    queryKey: demo ? ['public', 'teacher-demo', 'students'] : ['teacher', 'assigned-students'],
    queryFn: demo ? getDemoAssignedStudents : getAssignedStudents,
    staleTime: 60 * 1000,
  });

  const students = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return (data?.students ?? []).filter((student) => {
      if (grade && String(student.class_grade ?? '') !== grade) return false;
      if (classId && String(student.class_id ?? '') !== classId) return false;
      if (!keyword) return true;
      return [student.name, student.email, student.participant_code, student.class_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(keyword));
    });
  }, [classId, data?.students, grade, search]);

  const classes = useMemo(() => {
    const seen = new Map<number, string>();
    for (const student of data?.students ?? []) {
      if (grade && String(student.class_grade ?? '') !== grade) continue;
      if (student.class_id != null && student.class_name) seen.set(student.class_id, student.class_name);
    }
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1], 'ja'));
  }, [data?.students, grade]);

  return (
    <AdminLayout demo={demo}>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">担当学生</h1>
          <p className="mt-2 text-sm text-gray-600">
            学生を選ぶと、その学生の学習履歴と面接評価を確認できます。
          </p>
        </div>

        <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 sm:flex-row">
          <select
            value={grade}
            onChange={(event) => {
              setGrade(event.target.value);
              setClassId('');
            }}
            className="h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900"
            aria-label="学年で絞り込み"
          >
            <option value="">すべての学年</option>
            <option value="1">1年生</option>
            <option value="2">2年生</option>
            <option value="3">3年生</option>
          </select>
          <select
            value={classId}
            onChange={(event) => setClassId(event.target.value)}
            className="h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900"
            aria-label="担当クラスで絞り込み"
          >
            <option value="">すべてのクラス</option>
            {classes.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <div className="relative w-full max-w-xl">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="学籍番号・名前・メールアドレスで検索"
              className="h-11 pl-10"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            読み込み中...
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-red-700">
            担当学生を読み込めませんでした。
          </div>
        ) : students.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-600">
            {search || grade || classId
              ? '絞り込み条件に一致する担当学生はいません。'
              : '担当学生がまだ割り当てられていません。'}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {students.map((student) => (
              <button
                key={student.id}
                type="button"
                onClick={() => setLocation(teacherHref(demo, `/teacher/students/${student.id}`))}
                className="group flex min-h-[164px] flex-col rounded-lg border border-gray-200 bg-white p-5 text-left transition-colors hover:border-cyan-600 hover:bg-cyan-50 focus:outline-none focus:ring-2 focus:ring-cyan-700"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-cyan-50 text-cyan-800">
                      <GraduationCap className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-cyan-800">
                        {student.participant_code ? `学籍番号 ${student.participant_code}` : '学籍番号未登録'}
                      </div>
                      <div className="mt-1 truncate text-lg font-bold text-gray-900">{student.name}</div>
                      <div className="mt-1 text-xs text-gray-500">{student.class_name ?? 'クラス未所属'}</div>
                    </div>
                  </div>
                  <ChevronRight className="mt-2 h-5 w-5 shrink-0 text-gray-400 group-hover:text-cyan-800" />
                </div>

                <div className="mt-5 grid grid-cols-2 gap-2 text-sm">
                  <div className="rounded-md bg-gray-50 px-3 py-2">
                    <div className="text-xs text-gray-500">面接</div>
                    <div className="mt-1 font-bold text-gray-900">{student.interview_count}件</div>
                  </div>
                  <div className="rounded-md bg-gray-50 px-3 py-2">
                    <div className="text-xs text-gray-500">評価済み</div>
                    <div className="mt-1 font-bold text-gray-900">{student.scored_interview_count}件</div>
                  </div>
                </div>

                <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                  <Calendar className="h-4 w-4" />
                  最終面接: {formatLastActivity(student.last_interview_at)}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
